import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Workflow, Trash2, Mail, MessageSquare, Loader2, Clock, Zap } from "lucide-react";
import { toast } from "sonner";

const emptyStep = () => ({ channel: "email", delay_minutes: 0, subject: "", body: "" });

function AutomationDialog({ open, onOpenChange, automation, onSaved }) {
  const editing = !!automation;
  const [name, setName] = useState(automation?.name || "");
  const [enabled, setEnabled] = useState(automation?.enabled ?? true);
  const [steps, setSteps] = useState(automation?.steps?.length ? automation.steps.map((s) => ({ ...s })) : [emptyStep()]);
  const [loading, setLoading] = useState(false);

  const updateStep = (i, k, v) => setSteps((prev) => prev.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)));

  const save = async () => {
    if (!name) { toast.error("Name is required"); return; }
    setLoading(true);
    try {
      const payload = { name, enabled, trigger: "new_lead", steps: steps.map((s) => ({ ...s, delay_minutes: Number(s.delay_minutes) || 0 })) };
      if (editing) await api.put(`/automations/${automation.id}`, payload);
      else await api.post("/automations", payload);
      toast.success(editing ? "Automation updated" : "Automation created");
      onSaved();
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="automation-dialog">
        <DialogHeader><DialogTitle>{editing ? "Edit automation" : "New automation"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex items-end gap-4">
            <div className="flex-1"><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="automation-name" className="mt-1" placeholder="New Lead Welcome" /></div>
            <div className="flex items-center gap-2 pb-2"><Switch checked={enabled} onCheckedChange={setEnabled} data-testid="automation-enabled-switch" /><span className="text-sm">{enabled ? "Enabled" : "Disabled"}</span></div>
          </div>
          <div className="text-sm text-slate-500 bg-slate-50 rounded-lg p-3 flex items-center gap-2"><Zap className="h-4 w-4 text-amber-500" /> Trigger: <strong>When a new lead is created</strong></div>

          <div className="space-y-3">
            <Label>Follow-up steps</Label>
            {steps.map((s, i) => (
              <Card key={i} className="p-4 border-slate-200" data-testid={`automation-step-${i}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium">Step {i + 1}</span>
                  {steps.length > 1 && <button onClick={() => setSteps(steps.filter((_, idx) => idx !== i))} className="text-red-500" data-testid={`remove-step-${i}`}><Trash2 className="h-4 w-4" /></button>}
                </div>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <Label className="text-xs">Channel</Label>
                    <Select value={s.channel} onValueChange={(v) => updateStep(i, "channel", v)}>
                      <SelectTrigger className="mt-1" data-testid={`step-channel-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="email">Email</SelectItem><SelectItem value="sms">SMS</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Delay (minutes)</Label><Input type="number" value={s.delay_minutes} onChange={(e) => updateStep(i, "delay_minutes", e.target.value)} data-testid={`step-delay-${i}`} className="mt-1" /></div>
                </div>
                {s.channel === "email" && <div className="mb-3"><Label className="text-xs">Subject</Label><Input value={s.subject} onChange={(e) => updateStep(i, "subject", e.target.value)} data-testid={`step-subject-${i}`} className="mt-1" /></div>}
                <div><Label className="text-xs">Message</Label><Textarea value={s.body} onChange={(e) => updateStep(i, "body", e.target.value)} data-testid={`step-body-${i}`} className="mt-1" rows={2} placeholder="Use {{name}}, {{business}}, {{service}}" /></div>
              </Card>
            ))}
            <Button variant="outline" onClick={() => setSteps([...steps, emptyStep()])} data-testid="add-step-btn"><Plus className="h-4 w-4 mr-1" /> Add step</Button>
            <p className="text-xs text-slate-400">Variables: {"{{name}}"}, {"{{business}}"}, {"{{service}}"}. SMS sending is <strong>mocked</strong> until an SMS provider is connected; email sends via Resend.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={loading} data-testid="automation-save-btn" className="bg-blue-600 hover:bg-blue-700">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save automation"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Automations() {
  const { user } = useAuth();
  const canEdit = ["owner", "admin"].includes(user?.role);
  const [automations, setAutomations] = useState(null);
  const [history, setHistory] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get("/automations").then(({ data }) => setAutomations(data));
    api.get("/automations/history").then(({ data }) => setHistory(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (a) => { await api.put(`/automations/${a.id}`, { enabled: !a.enabled }); load(); };
  const remove = async (id) => { await api.delete(`/automations/${id}`); toast.success("Deleted"); load(); };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-heading">Automations</h1>
          <p className="text-slate-500 mt-1">Automatic follow-up for every new lead.</p>
        </div>
        {canEdit && <Button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="add-automation-btn" className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-1" /> New automation</Button>}
      </div>

      <Tabs defaultValue="workflows">
        <TabsList><TabsTrigger value="workflows" data-testid="tab-workflows">Workflows</TabsTrigger><TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger></TabsList>

        <TabsContent value="workflows" className="mt-4">
          {automations === null ? <Skeleton className="h-64 rounded-xl" /> : automations.length === 0 ? (
            <Card className="p-12 text-center border-slate-200" data-testid="automations-empty">
              <Workflow className="h-12 w-12 text-slate-300 mx-auto" />
              <h3 className="mt-4 font-heading font-semibold text-lg">No automations yet</h3>
              <p className="text-slate-500 mt-1">Create a follow-up sequence to respond to leads instantly.</p>
            </Card>
          ) : (
            <div className="space-y-4">
              {automations.map((a) => (
                <Card key={a.id} className="p-5 border-slate-200" data-testid={`automation-${a.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center"><Workflow className="h-5 w-5 text-blue-600" /></div>
                      <div>
                        <div className="font-semibold">{a.name}</div>
                        <div className="text-sm text-slate-500">{a.steps.length} step{a.steps.length !== 1 ? "s" : ""} · on new lead</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={a.enabled ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}>{a.enabled ? "Active" : "Paused"}</Badge>
                      {canEdit && <Switch checked={a.enabled} onCheckedChange={() => toggle(a)} data-testid={`toggle-automation-${a.id}`} />}
                    </div>
                  </div>
                  <div className="mt-4 space-y-2 pl-1">
                    {a.steps.map((s, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-slate-600">
                        {s.channel === "email" ? <Mail className="h-4 w-4 text-blue-500" /> : <MessageSquare className="h-4 w-4 text-emerald-500" />}
                        <Clock className="h-3.5 w-3.5 text-slate-400" />
                        <span className="text-slate-400">{s.delay_minutes === 0 ? "Immediately" : `after ${s.delay_minutes} min`}:</span>
                        <span className="truncate">{s.subject || s.body?.slice(0, 50)}</span>
                      </div>
                    ))}
                  </div>
                  {canEdit && (
                    <div className="mt-4 flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => { setEditing(a); setDialogOpen(true); }} data-testid={`edit-automation-${a.id}`}>Edit</Button>
                      <Button variant="outline" size="sm" className="text-red-600" onClick={() => remove(a.id)} data-testid={`delete-automation-${a.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          {history.length === 0 ? (
            <Card className="p-12 text-center border-slate-200" data-testid="history-empty"><Clock className="h-10 w-10 text-slate-300 mx-auto" /><p className="mt-3 text-slate-500">No automation activity yet.</p></Card>
          ) : (
            <div className="space-y-2">
              {history.map((h) => (
                <Card key={h.id} className="p-4 border-slate-200 flex items-center gap-3" data-testid={`history-${h.id}`}>
                  {h.channel === "email" ? <Mail className="h-4 w-4 text-blue-500" /> : <MessageSquare className="h-4 w-4 text-emerald-500" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{h.automation_name} → {h.lead_name}</div>
                    <div className="text-xs text-slate-500 truncate">{h.detail || h.subject}</div>
                  </div>
                  <Badge className={h.status === "sent" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : h.status === "pending" ? "bg-amber-100 text-amber-800 border-amber-200" : "bg-slate-100 text-slate-600 border-slate-200"}>{h.status}</Badge>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {dialogOpen && <AutomationDialog open={dialogOpen} onOpenChange={setDialogOpen} automation={editing} onSaved={load} />}
    </div>
  );
}
